import {
    sharingIssueSubTitle,
    sharingIssueSubTitlePostfix,
    sharingIssueSubTitleFromCloudyAttachment,
    sharingIssueSubTitleFromLink,
} from './getHasSharingIssues.locstring.json';
import loc, { format } from 'owa-localize';
import { getRecipientWellsFromComposeViewState } from './getAllRecipientsAsEmailAddressStrings';
import getCurrentFromAddress from './getFromAddressFromRecipientWell';
import { ComposeViewState } from 'owa-mail-compose-store';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { InfoBarMessageRank } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { InfoBarCustomAction } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import { getSharingDataFromLink, lazyGetSharingTipRecipientInfo } from 'owa-link-data';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import { composeGetHasSharingIssues } from 'owa-recipient-permission-checker';
import { lazyGetInfoBarCreator } from 'owa-sharing-data';

import {
    AttachmentWellViewState,
    lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend,
} from 'owa-attachment-well-data';
import {
    IdType,
    lazyGetSharingIssuesForSharingData,
    getSharingTipIdsList,
    SharingTipId,
    SharingTipInfo,
    SharingData,
    SharingTipRecipientInfo,
} from 'owa-sharing-data';

let getInfoBarCreator: (
    sharingData: SharingData[],
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    skipActionRecommendation: boolean,
    convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
) => InfoBarMessageViewStateCreator | null;
let getLastCloudyAttachmentsSharingIssueForBlockOnSend: (
    sharingTipIds: SharingTipId[],
    attachmentWellViewState: AttachmentWellViewState,
    recipientInfo: SharingTipRecipientInfo[],
    getInfoBarCreator: (
        sharingData: SharingData[],
        sharingTipId: SharingTipId,
        recipientInfo: SharingTipRecipientInfo[],
        skipActionRecommendation: boolean,
        convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
    ) => InfoBarMessageViewStateCreator | null
) => string;
let getSharingTipRecipientInfo: (
    recipientWells: RecipientWellWithFindControlViewState[],
    fromAddress: string
) => SharingTipRecipientInfo[];
let getSharingIssuesForSharingData: (
    sharingData: SharingData,
    recipientInfo: SharingTipRecipientInfo[]
) => SharingTipInfo[];
export async function getHasSharingIssues(viewState: ComposeViewState): Promise<boolean> {
    // log how many links are being sent. We want to do this even if we aren't checking for sharing issues
    const linkCount =
        viewState.attachmentWell && viewState.attachmentWell.sharingLinkIds
            ? viewState.attachmentWell.sharingLinkIds.length
            : 0;
    logUsage('ValidateSharingIssuesBeforeSend', {
        linkInBodyCount: linkCount,
    });

    // This is only being used for block on send for now, and this will tie its roll out to contextual tips
    // for links in the body. If this method is going to be re-used for something besides block on send
    // we should move this flight check to validateSend.ts and to doAnyLinksHaveSharingIssues
    if (!isFeatureEnabled('doc-attachment-contextualSharingTips')) {
        return false;
    }

    // If it hasn't been loaded, it means that either there is no sharing issues or it has failed to load.
    // Then we just return false as we don't want to delay the check for load failure.
    if (!lazyGetSharingTipRecipientInfo.isLoaded()) {
        return false;
    }

    composeGetHasSharingIssues(viewState.composeId);
    const sharingTipIds: SharingTipId[] = getSharingTipIdsList();
    const refAttachmentSharingIssues = viewState.infoBarIds.filter(
        id => (<string[]>sharingTipIds).indexOf(id) !== -1
    );

    if (!getSharingTipRecipientInfo) {
        getSharingTipRecipientInfo = await lazyGetSharingTipRecipientInfo.import();
    }

    let linkSharingIssues: SharingTipInfo[] = [];
    if (lazyGetSharingIssuesForSharingData.isLoaded()) {
        if (!getSharingIssuesForSharingData) {
            getSharingIssuesForSharingData = await lazyGetSharingIssuesForSharingData.import();
        }

        linkSharingIssues = getLinkSharingIssues(viewState);
    }
    return refAttachmentSharingIssues.length !== 0 || linkSharingIssues.length !== 0;
}

export async function preloadLazyImports() {
    if (!getLastCloudyAttachmentsSharingIssueForBlockOnSend) {
        getLastCloudyAttachmentsSharingIssueForBlockOnSend = await lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend.import();
    }

    if (!getInfoBarCreator) {
        getInfoBarCreator = await lazyGetInfoBarCreator.import();
    }
}

function getLinkSharingIssues(viewState: ComposeViewState): SharingTipInfo[] {
    let sharingTipsInfos: SharingTipInfo[] = [];
    if (!getSharingTipRecipientInfo || !getSharingIssuesForSharingData) {
        return sharingTipsInfos;
    }
    const linkIds = viewState.attachmentWell.sharingLinkIds;

    if (!linkIds || linkIds.length === 0) {
        return sharingTipsInfos;
    }

    const sharingData: SharingData[] = linkIds.map(id => getSharingDataFromLink(id));
    const recipientInfo: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
        getRecipientWellsFromComposeViewState(viewState),
        getCurrentFromAddress(viewState.toRecipientWell)
    );
    sharingData.forEach(function(data) {
        sharingTipsInfos = sharingTipsInfos.concat(
            getSharingIssuesForSharingData(data, recipientInfo).filter(
                issue => issue.messageRank === InfoBarMessageRank.Error
            )
        );
    });

    return sharingTipsInfos;
}

export function getSharingIssuesString(viewState: ComposeViewState): string | null {
    if (!getSharingTipRecipientInfo) {
        logUsage('genericSharingIssueBlockOnSendLazyImportFailure');
        return format(loc(sharingIssueSubTitle), loc(sharingIssueSubTitlePostfix));
    }

    const sharingTipIds: SharingTipId[] = getSharingTipIdsList();
    const refAttachmentSharingIssues = viewState.infoBarIds.filter(
        id => (<string[]>sharingTipIds).indexOf(id) !== -1
    );

    const linkSharingIssues: SharingTipInfo[] = getLinkSharingIssues(viewState);

    if (refAttachmentSharingIssues.length + linkSharingIssues.length === 1) {
        if (
            refAttachmentSharingIssues.length === 1 &&
            getInfoBarCreator &&
            getLastCloudyAttachmentsSharingIssueForBlockOnSend
        ) {
            const recipientInfo: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
                getRecipientWellsFromComposeViewState(viewState),
                getCurrentFromAddress(viewState.toRecipientWell)
            );
            const refAttachmentSharingIssue: string = getLastCloudyAttachmentsSharingIssueForBlockOnSend(
                sharingTipIds,
                viewState.attachmentWell,
                recipientInfo,
                getInfoBarCreator
            );
            if (refAttachmentSharingIssue) {
                logUsage('sharingIssueBlockOnSend', {
                    sharingTipId: refAttachmentSharingIssues[0],
                    errorSource: 'attachments',
                });
                return format(
                    loc(sharingIssueSubTitleFromCloudyAttachment),
                    refAttachmentSharingIssue,
                    loc(sharingIssueSubTitlePostfix)
                );
            }
        } else if (linkSharingIssues.length === 1) {
            logUsage('sharingIssueBlockOnSend', {
                sharingTipId: linkSharingIssues[0].id,
                errorSource: 'links',
            });
            return format(
                loc(sharingIssueSubTitleFromLink),
                linkSharingIssues[0].sharingTipString,
                loc(sharingIssueSubTitlePostfix)
            );
        }
    } else if (refAttachmentSharingIssues.length + linkSharingIssues.length > 1) {
        let errorSource: string = 'linksAndAttachments';
        if (refAttachmentSharingIssues.length === 0) {
            errorSource = 'links';
        } else if (linkSharingIssues.length === 0) {
            errorSource = 'attachments';
        }
        logUsage('sharingIssueBlockOnSend', { sharingTipId: 'multiple', errorSource: errorSource });
        return format(loc(sharingIssueSubTitle), loc(sharingIssueSubTitlePostfix));
    }

    return null;
}
